// Rumus antropometri & BMI.
// Sumber kategori IMT dewasa: Kemenkes RI / P2PTM (Asia-Pacific WHO),
// lihat Permenkes 41/2014 (Pedoman Gizi Seimbang). Untuk pasien anak
// (< 18 thn) kategori IMT/U memakai Z-score (Kepmenkes 1995/2010) sehingga
// kategori dewasa di sini tidak dipakai — pemanggil menandai `isPediatric`.

export type ImtCategory =
  | ""
  | "Sangat Kurus"
  | "Kurus"
  | "Normal"
  | "Gemuk"
  | "Obesitas"
  // legacy WHO-international values masih dipakai beberapa form lama
  | "Underweight"
  | "Overweight"
  | "Obese";

function toNumberOrNull(v: any): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function calcBmiNumber(tb: any, bb: any): number | null {
  const tbN = toNumberOrNull(tb);
  const bbN = toNumberOrNull(bb);
  if (tbN === null || bbN === null) return null;
  if (tbN <= 0 || bbN <= 0) return null;
  const tbM = tbN / 100;
  return bbN / (tbM * tbM);
}

// Hitung IMT (kg/m^2). Return string ber-2-desimal untuk kompatibilitas
// pemanggil lama yang langsung disimpan ke field text. Pakai
// `calcBmiNumber` jika butuh nilai numerik untuk perhitungan lanjutan.
export function calcBmi(tb: any, bb: any): string | null {
  const n = calcBmiNumber(tb, bb);
  return n === null ? null : n.toFixed(2);
}

// Kategori IMT mengikuti standar Kemenkes RI / P2PTM (Asia-Pacific):
//   < 17.0  : Sangat Kurus
//   17.0 – < 18.5 : Kurus
//   18.5 – 25.0   : Normal
//   > 25.0 – 27.0 : Gemuk (Overweight)
//   > 27.0  : Obesitas
// Untuk pasien anak (poli anak / usia < 18 thn), kategori dewasa tidak
// berlaku — return "" agar tampilan kosong dan klinisi memakai grafik z-score.
export function categoryBmiKemenkes(val: any, opts: { isPediatric?: boolean } = {}): ImtCategory {
  const v = toNumberOrNull(val);
  if (v === null || v <= 0) return "";
  if (opts.isPediatric) return "";
  if (v < 17.0) return "Sangat Kurus";
  if (v < 18.5) return "Kurus";
  if (v <= 25.0) return "Normal";
  if (v <= 27.0) return "Gemuk";
  return "Obesitas";
}

// Alias Indonesia agar konsisten dengan istilah UI ("Kategori IMT").
export const kategoriImt = categoryBmiKemenkes;

// Backward-compat: kategori WHO internasional yang dipakai komponen lama.
// Cutoff: <18.5 Underweight, <25 Normal, <30 Overweight, >=30 Obese.
// (Sebelumnya ada celah pada nilai persis 30 — diperbaiki di sini.)
export function categoryBmi(val: any): ImtCategory {
  const v = toNumberOrNull(val);
  if (v === null || v <= 0) return "";
  if (v < 18.5) return "Underweight";
  if (v < 25) return "Normal";
  if (v < 30) return "Overweight";
  return "Obese";
}

// Body Surface Area (Mosteller) — dipertahankan untuk pemanggil lama.
export function calcBSA(tb: any, bb: any): string | null {
  const tbN = toNumberOrNull(tb);
  const bbN = toNumberOrNull(bb);
  if (tbN === null || bbN === null) return null;
  if (tbN <= 0 || bbN <= 0) return null;
  return Math.sqrt((tbN * bbN) / 3600).toFixed(2);
}
